// src/components/admin/questions/QuestionsTab.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Search, Edit, Trash2, MoreVertical, Eye, EyeOff, FlaskConical,
    CheckSquare, Square, X, ChevronLeft, ChevronRight, BookLock, Download
} from 'lucide-react';
import { questionService } from '../../../services/questionService';
import QuestionFilters from '../../question/QuestionFilters';
import { QuestionFilters as Filters } from '../../../types/question.types';

const PAGE_SIZE = 20;

interface QuestionsTabProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
    onEditQuestion: (id: string) => void;
}

interface ConfirmState {
    title: string;
    message: string;
    confirmLabel?: string;
    onConfirm: () => void;
}

const ActionMenu: React.FC<{
    question: any;
    onEdit: () => void;
    onSetStatus: (s: 'publicado' | 'rascunho' | 'analise' | 'prova' | 'simulado') => void;
    onDelete: () => void;
}> = ({ question, onEdit, onSetStatus, onDelete }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div className="qt-action-menu" ref={ref}>
            <button className="qt-menu-trigger" onClick={() => setOpen(o => !o)} title="Ações">
                <MoreVertical size={16} />
            </button>
            {open && (
                <div className="qt-menu-dropdown">
                    <button className="qt-menu-item" onClick={() => { onEdit(); setOpen(false); }}>
                        <Edit size={13} /> Editar
                    </button>
                    {question.status !== 'publicado' && (
                        <button className="qt-menu-item qt-menu-item--publish" onClick={() => { onSetStatus('publicado'); setOpen(false); }}>
                            <Eye size={13} /> Publicar
                        </button>
                    )}
                    {question.status !== 'rascunho' && (
                        <button className="qt-menu-item" onClick={() => { onSetStatus('rascunho'); setOpen(false); }}>
                            <EyeOff size={13} /> Rascunho
                        </button>
                    )}
                    {question.status !== 'analise' && (
                        <button className="qt-menu-item qt-menu-item--analise" onClick={() => { onSetStatus('analise'); setOpen(false); }}>
                            <FlaskConical size={13} /> Em Análise
                        </button>
                    )}
                    {question.status !== 'prova' && (
                        <button className="qt-menu-item qt-menu-item--prova" onClick={() => { onSetStatus('prova'); setOpen(false); }}>
                            <BookLock size={13} /> Exclusiva Prova
                        </button>
                    )}
                    {question.status !== 'simulado' && (
                        <button className="qt-menu-item qt-menu-item--simulado" onClick={() => { onSetStatus('simulado'); setOpen(false); }}>
                            <BookLock size={13} /> Exclusiva Simulado
                        </button>
                    )}
                    <div className="qt-menu-divider" />
                    <button className="qt-menu-item qt-menu-item--delete" onClick={() => { onDelete(); setOpen(false); }}>
                        <Trash2 size={13} /> Deletar
                    </button>
                </div>
            )}
        </div>
    );
};

// ── Barra de ações em massa ──────────────────────────────────────
const BulkActionMenu: React.FC<{
    count: number;
    onAction: (action: string) => void;
    onClear: () => void;
    onExport: () => void;
}> = ({ count, onAction, onClear, onExport }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div className="qt-bulk-bar">
            <span className="qt-bulk-count">
                <CheckSquare size={15} />
                {count} selecionada{count !== 1 ? 's' : ''}
            </span>
            <div className="qt-bulk-actions">
                <button className="qt-bulk-btn qt-bulk-btn--export" onClick={onExport} title="Exportar selecionadas como JSON">
                    <Download size={13} /> Exportar
                </button>
                <div className="qt-bulk-menu-wrapper" ref={ref}>
                    <button className="qt-bulk-btn qt-bulk-btn--actions" onClick={() => setOpen(o => !o)}>
                        Ações <MoreVertical size={13} />
                    </button>
                    {open && (
                        <div className="qt-bulk-dropdown">
                            <button className="qt-bulk-dropdown-item qt-bulk-dropdown-item--publish"
                                onClick={() => { onAction('publicado'); setOpen(false); }}>
                                <Eye size={13} /> Publicar selecionadas
                            </button>
                            <button className="qt-bulk-dropdown-item"
                                onClick={() => { onAction('rascunho'); setOpen(false); }}>
                                <EyeOff size={13} /> Mover para rascunho
                            </button>
                            <button className="qt-bulk-dropdown-item qt-bulk-dropdown-item--analise"
                                onClick={() => { onAction('analise'); setOpen(false); }}>
                                <FlaskConical size={13} /> Enviar para análise
                            </button>
                            <button className="qt-bulk-dropdown-item qt-bulk-dropdown-item--prova"
                                onClick={() => { onAction('prova'); setOpen(false); }}>
                                <BookLock size={13} /> Marcar como exclusiva de prova
                            </button>
                            <button className="qt-bulk-dropdown-item qt-bulk-dropdown-item--simulado"
                                onClick={() => { onAction('simulado'); setOpen(false); }}>
                                <BookLock size={13} /> Marcar como exclusiva de simulado
                            </button>
                            <div className="qt-menu-divider" />
                            <button className="qt-bulk-dropdown-item qt-bulk-dropdown-item--delete"
                                onClick={() => { onAction('delete'); setOpen(false); }}>
                                <Trash2 size={13} /> Deletar selecionadas
                            </button>
                        </div>
                    )}
                </div>
                <button className="qt-bulk-btn qt-bulk-btn--clear" onClick={onClear}>
                    <X size={13} /> Limpar seleção
                </button>
            </div>
        </div>
    );
};

const QuestionsTab: React.FC<QuestionsTabProps> = ({ onShowToast, onEditQuestion }) => {
    const [questions, setQuestions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalQuestions, setTotalQuestions] = useState<number | undefined>(undefined);
    const [totalPages, setTotalPages] = useState(1);

    const [search, setSearch] = useState('');
    const [publishStatus, setPublishStatus] = useState('');
    const [page, setPage] = useState(1);
    const [advancedFilters, setAdvancedFilters] = useState<Filters>({});

    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const allSelected = questions.length > 0 && selectedIds.length === questions.length;
    const someSelected = selectedIds.length > 0 && !allSelected;

    const [confirmModal, setConfirmModal] = useState<ConfirmState | null>(null);
    const [exporting, setExporting] = useState(false);

    const toggleSelect = (id: string) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const toggleSelectAll = () => {
        setSelectedIds(allSelected ? [] : questions.map(q => q._id));
    };

    const loadQuestions = useCallback(async () => {
        try {
            setLoading(true);
            const data = await questionService.getQuestionsAdmin({
                search, status: publishStatus, page, limit: PAGE_SIZE, ...advancedFilters,
            });
            setQuestions(data.questions);
            setTotalQuestions(data.pagination?.total);
            setTotalPages(data.pagination?.pages ?? 1);
            setSelectedIds([]);
        } catch {
            onShowToast('Erro ao carregar questões', 'error');
        } finally {
            setLoading(false);
        }
    }, [search, publishStatus, page, advancedFilters, onShowToast]);

    useEffect(() => { loadQuestions(); }, [loadQuestions]);

    const handleAdvancedFiltersChange = (filters: Filters) => {
        setPage(1);
        setAdvancedFilters(filters);
    };

    const handleDelete = (id: string) => {
        setConfirmModal({
            title: 'Deletar questão',
            message: 'Esta ação é irreversível. Deseja realmente deletar esta questão?',
            confirmLabel: 'Deletar',
            onConfirm: async () => {
                setConfirmModal(null);
                try {
                    await questionService.deleteQuestion(id);
                    onShowToast('Questão deletada', 'success');
                    loadQuestions();
                } catch {
                    onShowToast('Erro ao deletar', 'error');
                }
            },
        });
    };

    const handleSetStatus = async (id: string, status: 'rascunho' | 'publicado' | 'analise' | 'prova' | 'simulado') => {
        try {
            await questionService.setQuestionStatus(id, status as any);
            const labels: Record<string, string> = {
                publicado: 'publicada', rascunho: 'movida para rascunho',
                analise: 'enviada para análise', prova: 'marcada como exclusiva de prova',
                simulado: 'marcada como exclusiva de simulado',
            };
            onShowToast(`Questão ${labels[status]}`, 'success');
            loadQuestions();
        } catch {
            onShowToast('Erro ao alterar status', 'error');
        }
    };

    const handleBulkAction = (action: string) => {
        if (selectedIds.length === 0) return;

        if (action === 'delete') {
            setConfirmModal({
                title: 'Deletar questões',
                message: `Deletar ${selectedIds.length} questão(ões) selecionada(s)? Esta ação não pode ser desfeita.`,
                confirmLabel: `Deletar ${selectedIds.length}`,
                onConfirm: async () => {
                    setConfirmModal(null);
                    let count = 0;
                    for (const id of selectedIds) {
                        try { await questionService.deleteQuestion(id); count++; } catch { /* continua */ }
                    }
                    onShowToast(`${count} questão(ões) deletada(s)`, 'success');
                    loadQuestions();
                },
            });
            return;
        }

        const status = action as 'publicado' | 'rascunho' | 'analise' | 'prova' | 'simulado';
        const labels: Record<string, string> = {
            publicado: 'publicadas', rascunho: 'movidas para rascunho',
            analise: 'enviadas para análise', prova: 'marcadas como exclusivas de prova',
            simulado: 'marcadas como exclusivas de simulado',
        };
        (async () => {
            let count = 0;
            for (const id of selectedIds) {
                try { await questionService.setQuestionStatus(id, status as any); count++; } catch { /* continua */ }
            }
            onShowToast(`${count} questão(ões) ${labels[status]}`, 'success');
            loadQuestions();
        })();
    };

    const handleExportJSON = useCallback(async () => {
        setExporting(true);
        try {
            let toExport: any[];

            if (selectedIds.length > 0) {
                toExport = questions.filter(q => selectedIds.includes(q._id));
            } else {
                const data = await questionService.getQuestionsAdmin({
                    search, status: publishStatus, ...advancedFilters, limit: 1000, page: 1,
                });
                toExport = data.questions;
            }

            const exportData = toExport.map(q => ({
                statement: q.statement,
                statementImage: q.statementImage || null,
                alternatives: (q.alternatives || []).map((a: any) => ({
                    letter: a.letter,
                    text: a.text,
                    isCorrect: a.isCorrect,
                })),
                explanationText: q.explanationText || null,
                explanationVideoUrl: q.explanationVideoUrl || null,
                category: q.category || {},
                difficulty: q.difficulty || 'medio',
                tags: q.tags || [],
            }));

            const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `questoes_graia_${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            onShowToast(`${exportData.length} questão(ões) exportada(s)`, 'success');
        } catch {
            onShowToast('Erro ao exportar questões', 'error');
        } finally {
            setExporting(false);
        }
    }, [selectedIds, questions, search, publishStatus, advancedFilters, onShowToast]);

    const statusLabel: Record<string, string> = {
        publicado: 'publicado', rascunho: 'rascunho', analise: 'em análise',
        prova: 'exclusiva prova', simulado: 'exclusiva simulado',
    };

    return (
        <div className="qm-block">
            <QuestionFilters onFiltersChange={handleAdvancedFiltersChange} totalQuestions={totalQuestions} loading={loading} />

            <div className="qm-block-filters">
                <div className="qm-search-box">
                    <Search size={16} />
                    <input
                        type="text"
                        placeholder="Buscar questões..."
                        value={search}
                        onChange={(e) => { setPage(1); setSearch(e.target.value); }}
                    />
                </div>
                <select value={publishStatus} onChange={(e) => { setPage(1); setPublishStatus(e.target.value); }}>
                    <option value="">Todos os status</option>
                    <option value="publicado">Publicadas</option>
                    <option value="rascunho">Rascunhos</option>
                    <option value="analise">Em Análise</option>
                    <option value="prova">Exclusivas de Prova</option>
                    <option value="simulado">Exclusivas de Simulado</option>
                </select>
                <button
                    className="qt-export-btn"
                    onClick={handleExportJSON}
                    disabled={exporting}
                    title="Exportar questões como JSON"
                >
                    <Download size={14} />
                    {exporting ? 'Exportando...' : 'Exportar JSON'}
                </button>
            </div>

            {selectedIds.length > 0 && (
                <BulkActionMenu
                    count={selectedIds.length}
                    onAction={handleBulkAction}
                    onClear={() => setSelectedIds([])}
                    onExport={handleExportJSON}
                />
            )}

            <div className="questions-table">
                {loading ? (
                    <div className="qm-loading">Carregando...</div>
                ) : questions.length === 0 ? (
                    <div className="qm-empty">Nenhuma questão encontrada</div>
                ) : (
                    <table>
                        <thead>
                            <tr>
                                <th className="qt-col-check">
                                    <button
                                        className={`qt-checkbox ${allSelected ? 'qt-checkbox--all' : someSelected ? 'qt-checkbox--some' : ''}`}
                                        onClick={toggleSelectAll}
                                        title={allSelected ? 'Desselecionar tudo' : 'Selecionar tudo'}
                                    >
                                        {allSelected ? <CheckSquare size={16} /> : someSelected ? <CheckSquare size={16} className="qt-checkbox-partial" /> : <Square size={16} />}
                                    </button>
                                </th>
                                <th>Enunciado</th>
                                <th>Categoria</th>
                                <th>Dificuldade</th>
                                <th>Status</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {questions.map((q) => (
                                <tr key={q._id} className={selectedIds.includes(q._id) ? 'qt-row--selected' : ''}>
                                    <td className="qt-col-check">
                                        <button
                                            className={`qt-checkbox ${selectedIds.includes(q._id) ? 'qt-checkbox--checked' : ''}`}
                                            onClick={() => toggleSelect(q._id)}
                                        >
                                            {selectedIds.includes(q._id) ? <CheckSquare size={16} /> : <Square size={16} />}
                                        </button>
                                    </td>
                                    <td className="qt-question-statement">
                                        {q.statement.replace(/<[^>]*>/g, '').substring(0, 100)}...
                                    </td>
                                    <td>{q.category?.level1}</td>
                                    <td>
                                        <span className={`badge-difficulty difficulty-${q.difficulty}`}>
                                            {q.difficulty}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`badge-status status-${q.status}`}>
                                            {statusLabel[q.status] || q.status}
                                        </span>
                                    </td>
                                    <td className="table-actions">
                                        <ActionMenu
                                            question={q}
                                            onEdit={() => onEditQuestion(q._id)}
                                            onSetStatus={(s) => handleSetStatus(q._id, s)}
                                            onDelete={() => handleDelete(q._id)}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ── Paginação ── */}
            {totalPages > 1 && (
                <div className="qt-pagination">
                    <button
                        className="qt-page-btn"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <span className="qt-page-info">
                        Página {page} de {totalPages}
                        {totalQuestions !== undefined && (
                            <span className="qt-page-total"> ({totalQuestions} total)</span>
                        )}
                    </span>
                    <button
                        className="qt-page-btn"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            )}

            {/* ── Modal de confirmação ── */}
            {confirmModal && (
                <div className="qt-confirm-overlay" onClick={() => setConfirmModal(null)}>
                    <div className="qt-confirm-modal" onClick={e => e.stopPropagation()}>
                        <h3>{confirmModal.title}</h3>
                        <p>{confirmModal.message}</p>
                        <div className="qt-confirm-actions">
                            <button className="qt-confirm-cancel" onClick={() => setConfirmModal(null)}>
                                Cancelar
                            </button>
                            <button className="qt-confirm-ok" onClick={confirmModal.onConfirm}>
                                {confirmModal.confirmLabel ?? 'Confirmar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuestionsTab;
